import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Select from "react-select";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import {
  clearInitAccountStatus,
  clearUpdateBusiness,
  requestGetBusiness,
  requestInitAccount,
  requestListAccount,
  requestUpdateBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { TAX_CATEGORIES } from "../../Constants";
import {
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Switch,
  Transition,
} from "@headlessui/react";
import { classNames, generateArrayOfYears, validateImage } from "../../Utils";
import FormLabel from "../../Components/FormLabel";
import { BUSINESS_CONFIG } from "../Business";
import EmptyState from "../../Components/EmptyState";
import {
  BUSINESS_CATEGORIES,
  BUSINESS_ENTITY_TYPES,
  BUSINESS_TYPES_OPTIONS,
  isBusinessWriteBlocked,
} from "../../Constants/business";
import BusinessStatusBanner from "../../Components/v2/Business/BusinessStatusBanner";

const years = generateArrayOfYears();

export const BusinessDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [name, setname] = useState("");
  const [type, settype] = useState("PERSONAL");
  const [entityType, setentityType] = useState("");
  const [category, setcategory] = useState(null);
  const [phone, setphone] = useState("");
  const [address, setaddress] = useState("");
  const [taxCategory, setaxcategory] = useState("");
  const [alwaysSync, setalwaysSync] = useState(false);
  const [cashIncomeDebitAccount, setcashIncomeDebitAccount] = useState(null);
  const [cashIncomeCreditAccount, setcashIncomeCreditAccount] = useState(null);
  const [cashExpenseDebitAccount, setcashExpenseDebitAccount] = useState(null);
  const [cashExpenseCreditAccount, setcashExpenseCreditAccount] =
    useState(null);
  // Discount accounts (2.7) — independent of the cash-book sync toggle.
  const [salesDiscountAccount, setsalesDiscountAccount] = useState(null);
  const [purchaseDiscountAccount, setpurchaseDiscountAccount] = useState(null);
  // True while a save triggered by "generate accounts" is in flight, so the
  // update-success effect chains init instead of showing the success modal.
  const [initAfterSave, setInitAfterSave] = useState(false);
  const [npwp, setnpwp] = useState("");
  const [yearRegistered, setyearRegistered] = useState("");
  const [email, setemail] = useState("");
  const [image, setimage] = useState(null);

  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [fieldError, setfieldError] = useState(false);
  const [mainBusinessActive, setmainBusinessActive] = useState(false);
  const [selectedConfig, setselectedConfig] = useState(null);

  const [businessError, setbusinessError] = useState(null);
  const [typeError, settypeError] = useState(null);
  const [phoneError, setphoneError] = useState(null);
  const [emailError, setemailError] = useState(null);
  const [imageError, setimageError] = useState(null);
  const [npwpError, setnpwpError] = useState(null);
  const [submitted, setsubmitted] = useState(false);

  const [premiumModalOpen, setpremiumModalOpen] = useState(false);
  const [premiumModalTitle, setpremiumModalTitle] = useState("");
  const [premiumModalDescription, setpremiumModalDescription] = useState("");
  const [premiumModalType, setpremiumModalType] = useState("");

  const cleanedPhone = phone?.replace(/\s/g, "");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const dispatch = useBusinessTransactionDispatch();
  const { error, loading, updateBusiness, business } =
    useBusinessTransactionState();
  // Restricted / free-limit-PENDING businesses are read-only (mirrors the
  // backend write guard and the mobile app). The banner explains why; the
  // profile Save and account-init actions below are gated on this.
  const writeBlocked = isBusinessWriteBlocked(business);
  const accountingDispatch = useAccountingDispatch();
  const {
    accounts,
    accountLoading,
    accountError,
    initAccountLoading,
    initAccountStatus,
    submitError,
  } = useAccountingState();

  const taxCategories = useMemo(() => {
    return TAX_CATEGORIES.map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t]);

  useEffect(() => {
    if (selectedConfig?.value) {
      const route = `${selectedConfig?.value}?id=${id}`;
      navigate(route);
    }
    setselectedConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfig?.value]);

  useEffect(() => {
    if (updateBusiness?.id) {
      // Save-then-init: profile persisted, now seed the chart from it instead
      // of showing the success modal. Clear so a later save can re-trigger.
      if (initAfterSave) {
        setInitAfterSave(false);
        clearUpdateBusiness(dispatch);
        requestInitAccount(accountingDispatch, {
          businessID: updateBusiness.id,
        });
        return;
      }
      setshowSuccessModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBusiness.id]);

  useEffect(() => {
    if (id) {
      requestGetBusiness(dispatch, { id });
    }
  }, [dispatch, id]);

  // Save-then-init: if the profile save failed, drop the pending flag so a
  // later successful save does not accidentally chain an init.
  useEffect(() => {
    if (initAfterSave && error) setInitAfterSave(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (id === user?.mainBusiness?.id) {
      setmainBusinessActive(true);
    }
  }, [id, user?.mainBusiness?.id]);

  useEffect(() => {
    if (business.name) {
      setname(business.name);
    }
    if (business.email) {
      setemail(business.email);
    }
    if (business.type) {
      settype(business.type);
    }
    if (business.structure) {
      const selectedEntityType = BUSINESS_ENTITY_TYPES.find(
        (item) => item.value === business.structure
      );
      setentityType(selectedEntityType);
    }
    if (business.category) {
      const selectedCategory = BUSINESS_CATEGORIES.find(
        (item) => item.value === business.category
      );
      setcategory(selectedCategory || null);
    }
    if (business.phone) {
      setphone(business.phone);
    }
    if (business.taxCategory) {
      const selectedTaxCategory = taxCategories.find(
        (item) => item.value === business.taxCategory
      );
      setaxcategory(selectedTaxCategory);
    }
    if (business.address) {
      setaddress(business.address);
    }
    if (business.taxNumber) {
      setnpwp(business.taxNumber);
    }
    if (business.yearRegistered) {
      const selectedYearRegistered = years.find(
        (item) => item.value === business.yearRegistered
      );
      setyearRegistered(selectedYearRegistered);
    }
    if (business.alwaysSync) {
      setalwaysSync(true);
    }
    if (business.image?.key) {
      setimage("");
    }
  }, [business, taxCategories]);

  useEffect(() => {
    if (business && alwaysSync) {
      const {
        cashExpenseCreditAccount,
        cashExpenseDebitAccount,
        cashIncomeCreditAccount,
        cashIncomeDebitAccount,
      } = business;
      if (cashExpenseCreditAccount)
        setcashExpenseCreditAccount({
          ...cashExpenseCreditAccount,
          value: cashExpenseCreditAccount.id,
          label: `${cashExpenseCreditAccount.code} - ${cashExpenseCreditAccount.name}`,
        });
      if (cashExpenseDebitAccount)
        setcashExpenseDebitAccount({
          ...cashExpenseDebitAccount,
          value: cashExpenseDebitAccount.id,
          label: `${cashExpenseDebitAccount.code} - ${cashExpenseDebitAccount.name}`,
        });
      if (cashIncomeCreditAccount)
        setcashIncomeCreditAccount({
          ...cashIncomeCreditAccount,
          value: cashIncomeCreditAccount.id,
          label: `${cashIncomeCreditAccount.code} - ${cashIncomeCreditAccount.name}`,
        });
      if (cashIncomeDebitAccount)
        setcashIncomeDebitAccount({
          ...cashIncomeDebitAccount,
          value: cashIncomeDebitAccount.id,
          label: `${cashIncomeDebitAccount.code} - ${cashIncomeDebitAccount.name}`,
        });
    }
  }, [business, alwaysSync]);

  // Hydrate discount accounts regardless of alwaysSync (they are not tied to
  // the cash-book sync mapping).
  useEffect(() => {
    if (business?.salesDiscountAccount)
      setsalesDiscountAccount({
        ...business.salesDiscountAccount,
        value: business.salesDiscountAccount.id,
        label: `${business.salesDiscountAccount.code} - ${business.salesDiscountAccount.name}`,
      });
    if (business?.purchaseDiscountAccount)
      setpurchaseDiscountAccount({
        ...business.purchaseDiscountAccount,
        value: business.purchaseDiscountAccount.id,
        label: `${business.purchaseDiscountAccount.code} - ${business.purchaseDiscountAccount.name}`,
      });
  }, [business]);

  useEffect(() => {
    if (submitted) {
      const isBusinessValid = Boolean(name);
      const isTypeValid = Boolean(type);

      if (isBusinessValid) {
        setErrorMessage("business", "");
      } else {
        setErrorMessage("business", "Business name is required.");
      }

      if (isTypeValid) {
        setErrorMessage("type", "");
      } else {
        setErrorMessage("type", "Business Type name is required.");
      }
    }
  }, [name, type, submitted]);

  useEffect(() => {
    if (submitted && phone) {
      const isPhoneValid = cleanedPhone?.length >= 10;
      if (isPhoneValid) {
        setErrorMessage("phone", "");
      } else {
        setErrorMessage("phone", "Please enter a valid phone number.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, submitted]);

  useEffect(() => {
    if (submitted && npwp) {
      const isNpwpValid = npwp.length >= 15 && npwp.length <= 16;
      if (isNpwpValid) {
        setErrorMessage("npwp", "");
      } else {
        setErrorMessage("npwp", "Invalid NPWP. Enter 15-16 digits.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npwp, submitted]);

  useEffect(() => {
    validateEmail(email, submitted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, submitted]);

  const validateEmail = (email, submitted) => {
    if (submitted && email) {
      const isValid = emailRegex.test(email);
      setErrorMessage(
        "email",
        isValid ? "" : "Please enter a valid email address."
      );
    }
  };

  useEffect(() => {
    // Load accounts in edit mode regardless of alwaysSync: the discount-account
    // pickers need them too (not just the cash-book sync mapping).
    if (business?.id) {
      requestListAccount(accountingDispatch, {
        businessID: business.id,
        limit: 100,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      requestListAccount(accountingDispatch, {
        businessID: business.id,
        limit: 100,
      });
      clearInitAccountStatus(accountingDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  useEffect(() => {
    if (type !== "BUSINESS") {
      setentityType("");
    }
  }, [type]);

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "business":
        setbusinessError(message);
        break;
      case "type":
        settypeError(message);
        break;
      case "phone":
        setphoneError(message);
        break;
      case "email":
        setemailError(message);
        break;
      case "image":
        setimageError(message);
        break;
      case "npwp":
        setnpwpError(message);
        break;
      default:
        break;
    }
  };

  const validateInput = () => {
    const isBusinessValid = Boolean(name);
    const isTypeValid = Boolean(type);
    const isPhoneValid = !phone || cleanedPhone?.length >= 10;
    const isEmailValid = !email || emailRegex.test(email);
    const isNpwpValid = !npwp || (npwp.length >= 15 && npwp.length <= 16);

    return (
      isBusinessValid &&
      isTypeValid &&
      isPhoneValid &&
      isEmailValid &&
      isNpwpValid
    );
  };

  const handleNameChange = (event) => {
    setname(event.target.value);
  };

  const handleTypeChange = (event) => {
    settype(event.target.value);
  };

  const handleTaxChange = (event) => {
    setaxcategory(event);
  };

  const handleAddressChange = (event) => {
    setaddress(event.target.value);
  };

  const navigateToBusiness = () => {
    setshowSuccessModal(false);
    if (!error) {
      if (updateBusiness?.id) {
        window.location.href = "/business";
      }
    }
  };

  const onSave = (event) => {
    event.preventDefault();
    if (writeBlocked) return;
    setsubmitted(true);
    const valid = validateInput();

    if (valid) {
      setfieldError(false);
      requestUpdateBusiness(dispatch, {
        input: buildEditInput(),
        ...(mainBusinessActive && { businessID: id }),
      });
    } else {
      setfieldError(true);
    }
  };

  // Build the update input from current form state. Shared by Save and the
  // save-then-init flow so the seeded chart matches what the user sees (the
  // backend reads structure/type/category off the Business record).
  const buildEditInput = () => {
    const input = {
      id,
      name,
      email,
      type,
      structure: entityType?.value || null,
      category: category?.value || null,
      phone,
      taxCategory: taxCategory?.value || null,
      address,
      taxNumber: npwp,
      yearRegistered: yearRegistered?.value || null,
      alwaysSync,
      cashIncomeDebitAccountID: alwaysSync ? cashIncomeDebitAccount?.value : null,
      cashIncomeCreditAccountID: alwaysSync
        ? cashIncomeCreditAccount?.value
        : null,
      cashExpenseDebitAccountID: alwaysSync
        ? cashExpenseDebitAccount?.value
        : null,
      cashExpenseCreditAccountID: alwaysSync
        ? cashExpenseCreditAccount?.value
        : null,
      salesDiscountAccountID: salesDiscountAccount?.value || null,
      purchaseDiscountAccountID: purchaseDiscountAccount?.value || null,
    };
    if (image) {
      input.image = image;
    }
    if (image === null) {
      input.image = null;
    }
    return input;
  };

  const handleInitAccount = () => {
    // Save-then-init: persist the current form profile first, then seed the
    // chart, so an unsaved category/type change is reflected. The update-
    // success effect chains requestInitAccount when initAfterSave is set.
    if (writeBlocked) return;
    if (!business?.id) {
      requestInitAccount(accountingDispatch, { businessID: business?.id });
      return;
    }
    setsubmitted(true);
    if (!validateInput()) return;
    setInitAfterSave(true);
    requestUpdateBusiness(dispatch, {
      input: buildEditInput(),
      ...(mainBusinessActive && { businessID: id }),
    });
  };

  const handleProfileLogoChange = (event) => {
    const selectedFile = event.target.files[0];
    const allowedFormats = ["image/jpeg", "image/png", "image/gif"];
    const maxSizeMB = 1;

    const validation = validateImage(selectedFile, maxSizeMB, allowedFormats);

    if (!validation.isValid) {
      setErrorMessage("image", validation.errorMessage);
      setimage(null);
    } else {
      setimage(selectedFile);
      setErrorMessage("image", "");
    }
  };

  const handleNoPremium = () => {
    setpremiumModalType("WARNING");
    setpremiumModalTitle(t("Premium Feature"));
    setpremiumModalDescription(
      t(
        "Only premium members can use the feature to change their business logo."
      )
    );
    setpremiumModalOpen(true);
  };

  const renderProfileLogo = () => {
    if (image === null) {
      return <NoLogo />;
    }

    if (image) {
      return (
        <img
          src={URL.createObjectURL(image)}
          alt={t("Profile")}
          className="rounded-lg object-contain w-24 h-24 max-h-[100px]"
        />
      );
    }

    if (business.imageUrl) {
      return (
        <img
          src={business.imageUrl}
          alt={t("Profile")}
          className="rounded-lg object-contain w-24 h-24 max-h-[100px]"
        />
      );
    }

    return <NoLogo />;
  };

  function NoLogo() {
    return (
      <span className="rounded-lg w-24 h-24 overflow-hidden bg-gray-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 180.119 139.794"
          fill="currentColor"
          className="h-full w-full text-gray-300"
        >
          <g
            transform="translate(-13.59 -66.639)"
            paintOrder="fill markers stroke"
          >
            <path
              d="m118.507 133.514-34.249 34.249-15.968-15.968-41.938 41.937H178.726z"
              opacity=".675"
              fill="#C3C4C4"
            />
            <circle
              cx="58.217"
              cy="108.555"
              r="11.773"
              opacity=".675"
              fill="#C3C4C4"
            />
            <path fill="none" d="M26.111 77.634h152.614v116.099H26.111z" />
          </g>
        </svg>
      </span>
    );
  }

  const InputSkeleton = ({ rows }) => {
    const divElements = Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="animate-pulse">
        <div className="flex-1 space-y-2">
          <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
          <div className="block h-10 bg-gray-300 rounded-md"></div>
        </div>
      </div>
    ));

    return <>{divElements}</>;
  };

  function ProfileLogoSkeleton() {
    return (
      <div className="animate-pulse col-span-12 flex gap-8 items-center">
        <div className="rounded-lg bg-gray-300 w-24 h-24" />
        <div>
          <div className="flex">
            <div className="w-16 h-10 bg-gray-300 rounded-md"></div>
            <div className="w-16 h-10 bg-gray-300 rounded-md ml-3"></div>
          </div>
          <div className="w-36 h-4 bg-gray-300 rounded-md mt-2"></div>
        </div>
      </div>
    );
  }

  const EmptyAccount = () => {
    return initAccountLoading ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <InputSkeleton rows={4} />
      </div>
    ) : (
      <EmptyState
        className="max-w-full"
        titleKey="No Accounts"
        descriptionKey="Please create an account first"
      >
        <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-6">
          <Button
            outline
            className="flex-1"
            onClick={handleInitAccount}
            disabled={writeBlocked}
            title="Create Automatically"
          />

          <Button
            outline
            className="flex-1"
            onClick={() => navigate(`/account/import?id=${business.id}`)}
            disabled={writeBlocked}
            title="Import Using XLSX File"
          />
        </div>
      </EmptyState>
    );
  };

  if (error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error?.message || "Error update business."}
          errors={error?.errors}
        />
      </div>
    );
  }

  const sortAccountCode = (a, b) => {
    const aCode = parseInt(a.code, 10);
    const bCode = parseInt(b.code, 10);
    return (aCode < 400000) - (bCode < 400000) || aCode - bCode;
  };

  return (
    <div className="space-y-4">
      {business?.id && <BusinessStatusBanner business={business} />}
      <div>
        <Listbox value={selectedConfig} onChange={setselectedConfig}>
          {({ open }) => (
            <>
              <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                <span className="ml-3 mr-2">
                  {t(selectedConfig?.label || t("Settings"))}
                </span>
                <SelectorIcon className="w-5 h-5 text-gray-500" />
              </ListboxButton>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                  {BUSINESS_CONFIG.map((report) => (
                    <ListboxOption
                      key={report.value}
                      className={({ focus }) =>
                        classNames(
                          focus ? "text-white bg-blue-600" : "text-gray-900",
                          "cursor-default select-none relative py-2 pl-3 pr-9"
                        )
                      }
                      value={report}
                    >
                      {({ selected, focus }) => (
                        <>
                          <div className="flex items-center justify-between">
                            <span
                              className={classNames(
                                selected ? "font-semibold" : "font-normal",
                                "ml-3 mr-2 block truncate"
                              )}
                            >
                              {t(report.label)}
                            </span>
                            {selected ? (
                              <span
                                className={classNames(
                                  focus ? "text-white" : "text-blue-600",
                                  "absolute inset-y-0 right-0 flex items-center pr-4"
                                )}
                              >
                                <CheckIcon className="h-5 w-5" />
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </>
          )}
        </Listbox>
      </div>
      <div className="bg-white shadow sm:overflow-hidden rounded-md sm:rounded-lg">
        {submitError && (
          <div className="px-4 pt-6 sm:px-6">
            <Alert
              type="error"
              title="Error"
              message={submitError?.message || "Error"}
              errors={submitError?.errors}
            />
          </div>
        )}
        <ModalConfirm
          open={
            premiumModalOpen &&
            premiumModalTitle !== "" &&
            premiumModalDescription !== ""
          }
          setOpen={(open) => {
            setpremiumModalOpen(open);
          }}
          title={premiumModalTitle}
          description={premiumModalDescription}
          type={premiumModalType}
          confirmTitle={t("Subscribe")}
          onConfirm={() => {
            navigate("/subscription");
          }}
        />
        <form action="#" onSubmit={onSave}>
          <div className="space-y-6 px-4 py-5 sm:p-6">
            {updateBusiness?.id && (
              <ModalConfirm
                open={showSuccessModal}
                setOpen={(open) => {
                  setshowSuccessModal(open);
                }}
                title="Success"
                description="Your business has been updated."
                type="SUCCESS"
                onConfirm={navigateToBusiness}
              />
            )}
            {fieldError && (
              <Alert
                title="Incomplete Form"
                type="warning"
                message="Please fill out all fields."
              />
            )}
            <div>
              {loading ? (
                <ProfileLogoSkeleton />
              ) : (
                <div className="flex-col gap-4">
                  <div className="col-span-12 flex gap-8 items-center">
                    {renderProfileLogo()}
                    <div>
                      <div className="flex">
                        <div className="relative bg-white py-2 px-3 border border-blue-gray-300 rounded-md shadow-sm flex items-center cursor-pointer hover:bg-blue-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-blue-gray-50 focus-within:ring-blue-500">
                          <label
                            htmlFor="user-photo"
                            className="relative text-sm font-medium text-blue-gray-900 pointer-events-none"
                          >
                            <span>{t("Change")}</span>
                            <span className="sr-only">
                              {" "}
                              {t("profile photo")}
                            </span>
                          </label>
                          {user && user.membership === "PREMIUM" ? (
                            <input
                              id="user-photo"
                              name="user-photo"
                              type="file"
                              accept="image/jpeg, image/png, image/gif"
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md"
                              onChange={handleProfileLogoChange}
                            />
                          ) : (
                            <button
                              id="user-photo"
                              name="user-photo"
                              type="button"
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md"
                              onClick={handleNoPremium}
                            />
                          )}
                        </div>
                        <button
                          onClick={() => setimage(null)}
                          type="button"
                          className="ml-3 bg-transparent py-2 px-3 border border-transparent rounded-md text-sm font-medium text-blue-gray-900 hover:text-blue-gray-700 focus:outline-none focus:border-blue-gray-300 focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-gray-50 focus:ring-blue-500"
                        >
                          {t("Remove")}
                        </button>
                      </div>
                      <p className="text-gray-600 text-xs mt-2">
                        {image?.name
                          ? image.name
                          : t("JPG, GIF or PNG. 1MB max.")}
                      </p>
                    </div>
                  </div>
                  {imageError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(imageError)}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {loading ? (
                <InputSkeleton rows={9} />
              ) : (
                <>
                  <div>
                    <FormLabel
                      htmlFor="name"
                      label={t("Business Name")}
                      required
                    />
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={handleNameChange}
                      placeholder={t("Business Name")}
                      className={classNames(
                        businessError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                    />
                    {businessError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(businessError)}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="type"
                      label={t("Business Type")}
                      required
                    />
                    <select
                      id="type"
                      name="type"
                      value={type}
                      onChange={handleTypeChange}
                      className={classNames(
                        typeError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md border bg-white py-2 px-3 shadow-sm"
                      )}
                    >
                      {BUSINESS_TYPES_OPTIONS.map((businessType) => (
                        <option
                          key={businessType.value}
                          value={businessType.value}
                        >
                          {t(businessType.label)}
                        </option>
                      ))}
                    </select>
                    {typeError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(typeError)}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="entityType"
                      label={t("businessEntityType")}
                    />
                    <Select
                      name="entityType"
                      className="mt-1 text-sm"
                      classNamePrefix="select2-selection"
                      options={BUSINESS_ENTITY_TYPES}
                      placeholder={t("businessEntityInputPlaceholder")}
                      value={entityType}
                      onChange={(value) => {
                        setentityType(value);
                      }}
                      isDisabled={type !== "BUSINESS"}
                      isClearable
                    />
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="category"
                      label={t("businessCategory", "Kategori Usaha")}
                    />
                    <Select
                      name="category"
                      className="mt-1 text-sm"
                      classNamePrefix="select2-selection"
                      options={BUSINESS_CATEGORIES}
                      placeholder={t(
                        "businessCategoryPlaceholder",
                        "Pilih Kategori Usaha"
                      )}
                      value={category}
                      onChange={setcategory}
                      isClearable
                    />
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="taxCategory"
                      label={t("Annual Revenue")}
                    />
                    <Select
                      name="taxCategory"
                      options={TAX_CATEGORIES.map((category) => ({
                        value: category?.value,
                        label: t(category?.label),
                      }))}
                      value={taxCategory}
                      onChange={handleTaxChange}
                      className="mt-1 text-sm"
                      classNamePrefix="select2-selection"
                      placeholder={t("Select Annual Revenue")}
                      isClearable
                    />
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="npwp"
                      label={t("Tax Identification Number (NPWP)")}
                    />
                    <NumericFormat
                      type="text"
                      name="npwp"
                      id="npwp"
                      allowLeadingZeros
                      className={classNames(
                        npwpError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                      placeholder={t(
                        "Enter the tax identification number (NPWP)"
                      )}
                      displayType="input"
                      value={npwp || ""}
                      onValueChange={(value) => {
                        setnpwp(value.value);
                      }}
                      isAllowed={({ value }) => value.length <= 16}
                    />
                    {npwpError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(npwpError)}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel htmlFor="year" label={t("npwpYearRegistered")} />
                    <Select
                      name="year"
                      className="mt-1 text-sm"
                      classNamePrefix="select2-selection"
                      options={years}
                      placeholder={t("npwpYearRegisteredPlaceholder")}
                      value={yearRegistered}
                      onChange={(value) => {
                        setyearRegistered(value);
                      }}
                      isClearable
                    />
                  </div>
                  <div>
                    <FormLabel htmlFor="email" label={t("Email")} />
                    <input
                      type="email"
                      name="email"
                      value={email}
                      placeholder={t("Enter email")}
                      onChange={(e) => setemail(e.target.value)}
                      className={classNames(
                        emailError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                    />
                    {emailError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(emailError)}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel htmlFor="phone" label={t("Phone Number")} />
                    <FormPhoneInput
                      id="phone"
                      name="phone"
                      value={phone}
                      onChange={setphone}
                      placeholder={t("Enter Phone Number")}
                      className={classNames(
                        phoneError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                    />
                    {phoneError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(phoneError)}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel htmlFor="address" label={t("Address")} />
                    <input
                      id="address"
                      type="text"
                      name="address"
                      value={address}
                      onChange={handleAddressChange}
                      placeholder={t("Enter Address")}
                      className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="mainbusiness-enabled"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("Main Business")}
                    </label>
                    <Field
                      as="div"
                      className="flex items-center mt-1 py-2 px-3 border border-gray-300 rounded-md"
                    >
                      <Switch
                        checked={mainBusinessActive}
                        onChange={setmainBusinessActive}
                        className={classNames(
                          mainBusinessActive ? "bg-blue-600" : "bg-gray-200",
                          "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        )}
                      >
                        <span className="sr-only">Use setting</span>
                        <span
                          className={classNames(
                            mainBusinessActive
                              ? "translate-x-5"
                              : "translate-x-0",
                            "pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                          )}
                        >
                          <span
                            className={classNames(
                              mainBusinessActive
                                ? "opacity-0 ease-out duration-100"
                                : "opacity-100 ease-in duration-200",
                              "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            )}
                            aria-hidden="true"
                          >
                            <svg
                              className="h-3 w-3 text-gray-400"
                              fill="none"
                              viewBox="0 0 12 12"
                            >
                              <path
                                d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span
                            className={classNames(
                              mainBusinessActive
                                ? "opacity-100 ease-in duration-200"
                                : "opacity-0 ease-out duration-100",
                              "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            )}
                            aria-hidden="true"
                          >
                            <svg
                              className="h-3 w-3 text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 12 12"
                            >
                              <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                            </svg>
                          </span>
                        </span>
                      </Switch>
                    </Field>
                  </div>
                  <div>
                    <label
                      htmlFor="accounting-sync-enabled"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("Sync")}
                    </label>
                    <Field
                      as="div"
                      className="flex items-center mt-1 py-2 px-3 border border-gray-300 rounded-md"
                    >
                      <Switch
                        checked={alwaysSync}
                        onChange={setalwaysSync}
                        className={classNames(
                          alwaysSync ? "bg-blue-600" : "bg-gray-200",
                          "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        )}
                      >
                        <span className="sr-only">Use setting</span>
                        <span
                          className={classNames(
                            alwaysSync ? "translate-x-5" : "translate-x-0",
                            "pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                          )}
                        >
                          <span
                            className={classNames(
                              alwaysSync
                                ? "opacity-0 ease-out duration-100"
                                : "opacity-100 ease-in duration-200",
                              "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            )}
                            aria-hidden="true"
                          >
                            <svg
                              className="h-3 w-3 text-gray-400"
                              fill="none"
                              viewBox="0 0 12 12"
                            >
                              <path
                                d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span
                            className={classNames(
                              alwaysSync
                                ? "opacity-100 ease-in duration-200"
                                : "opacity-0 ease-out duration-100",
                              "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            )}
                            aria-hidden="true"
                          >
                            <svg
                              className="h-3 w-3 text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 12 12"
                            >
                              <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                            </svg>
                          </span>
                        </span>
                      </Switch>
                      <Label as="span" className="ml-3">
                        <div>
                          <span className="text-sm text-gray-900">
                            {t("Always Synchronize Cash Book & Accounting")}
                          </span>
                        </div>
                      </Label>
                    </Field>
                  </div>
                </>
              )}
            </div>
            {/* Account mapping (edit only) — one section holding the cash-book
                sub-group (sync only) and the discount sub-group (always),
                sharing a single empty-state prompt. */}
            {business?.id &&
              (accountLoading || loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
                  <InputSkeleton rows={alwaysSync ? 6 : 2} />
                </div>
              ) : accountError ? (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <Alert
                    title="Error"
                    type="error"
                    message={
                      accountError?.message || "Error fetch accounting account."
                    }
                    errors={accountError?.errors}
                  />
                </div>
              ) : accounts.length === 0 ? (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <EmptyAccount />
                </div>
              ) : (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  {alwaysSync && (
                    <div className="mb-6">
                      <p className="text-sm font-medium text-gray-900 mb-4">
                        {t("business.groups.cashBookMapping", "Pemetaan Buku Kas")}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label
                            htmlFor="cashIncomeDebitAccount"
                            className="block text-sm font-medium text-gray-700"
                          >
                            {t("Debit Account Income (Cash Book)")}
                          </label>
                          <Select
                            name="cashIncomeDebitAccount"
                            className="mt-1 text-sm"
                            classNamePrefix="select2-selection"
                            options={accounts
                              .filter((account) =>
                                ["CASH_AND_BANK", "INVENTORY"].includes(
                                  account.category
                                )
                              )
                              .sort(sortAccountCode)
                              .map((account) => ({
                                value: account.id,
                                label: `${account.code} - ${account.name}`,
                              }))}
                            value={cashIncomeDebitAccount}
                            onChange={setcashIncomeDebitAccount}
                            placeholder={t("Choose Debit Account")}
                            menuPortalTarget={document.body}
                            maxMenuHeight={150}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="cashIncomeCreditAccount"
                            className="block text-sm font-medium text-gray-700"
                          >
                            {t("Credit Account Income (Cash Book)")}
                          </label>
                          <Select
                            name="cashIncomeCreditAccount"
                            className="mt-1 text-sm"
                            classNamePrefix="select2-selection"
                            options={accounts
                              .filter((account) =>
                                ["INCOME", "OTHER_INCOME"].includes(
                                  account.category
                                )
                              )
                              .sort(sortAccountCode)
                              .map((account) => ({
                                value: account.id,
                                label: `${account.code} - ${account.name}`,
                              }))}
                            value={cashIncomeCreditAccount}
                            onChange={setcashIncomeCreditAccount}
                            placeholder={t("Choose Credit Account")}
                            menuPortalTarget={document.body}
                            maxMenuHeight={150}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="cashExpenseDebitAccount"
                            className="block text-sm font-medium text-gray-700"
                          >
                            {t("Debit Account Expense (Cash Book)")}
                          </label>
                          <Select
                            name="cashExpenseDebitAccount"
                            className="mt-1 text-sm"
                            classNamePrefix="select2-selection"
                            options={accounts
                              .filter((account) =>
                                [
                                  "COST_OF_GOODS_SOLD",
                                  "EXPENSE",
                                  "OTHER_EXPENSE",
                                  "INVENTORY",
                                  "CASH_AND_BANK",
                                  "ACCOUNTS_RECEIVABLE",
                                  "FIXED_ASSETS",
                                ].includes(account.category)
                              )
                              .sort(sortAccountCode)
                              .map((account) => ({
                                value: account.id,
                                label: `${account.code} - ${account.name}`,
                              }))}
                            value={cashExpenseDebitAccount}
                            onChange={setcashExpenseDebitAccount}
                            placeholder={t("Choose Debit Account")}
                            menuPortalTarget={document.body}
                            maxMenuHeight={150}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="cashExpenseCreditAccount"
                            className="block text-sm font-medium text-gray-700"
                          >
                            {t("Credit Account Expense (Cash Book)")}
                          </label>
                          <Select
                            name="cashExpenseCreditAccount"
                            className="mt-1 text-sm"
                            classNamePrefix="select2-selection"
                            options={accounts
                              .slice()
                              .filter((account) =>
                                [
                                  "CASH_AND_BANK",
                                  "INVENTORY",
                                  "OTHER_CURRENT_ASSETS",
                                  "FIXED_ASSETS",
                                  "OTHER_ASSETS",
                                ].includes(account.category)
                              )
                              .sort(sortAccountCode)
                              .map((account) => ({
                                value: account.id,
                                label: `${account.code} - ${account.name}`,
                              }))}
                            value={cashExpenseCreditAccount}
                            onChange={setcashExpenseCreditAccount}
                            placeholder={t("Choose Credit Account")}
                            menuPortalTarget={document.body}
                            maxMenuHeight={150}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Divider only when the cash-book sub-group is also shown. */}
                  <div
                    className={
                      alwaysSync ? "pt-6 border-t border-gray-100" : ""
                    }
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {t("business.groups.discountAccounts", "Akun Diskon")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 mb-4">
                      {t(
                        "business.groups.discountAccountsHelp",
                        "Kosongkan untuk memakai akun bawaan 440100 / 550100."
                      )}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="salesDiscountAccount"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t(
                            "business.fields.salesDiscountAccount",
                            "Akun Diskon Penjualan"
                          )}
                        </label>
                        <Select
                          name="salesDiscountAccount"
                          className="mt-1 text-sm"
                          classNamePrefix="select2-selection"
                          isClearable
                          options={accounts
                            .filter((account) =>
                              ["INCOME", "OTHER_INCOME"].includes(
                                account.category
                              )
                            )
                            .sort(sortAccountCode)
                            .map((account) => ({
                              value: account.id,
                              label: `${account.code} - ${account.name}`,
                            }))}
                          value={salesDiscountAccount}
                          onChange={setsalesDiscountAccount}
                          placeholder={t("Choose Account")}
                          menuPortalTarget={document.body}
                          maxMenuHeight={150}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="purchaseDiscountAccount"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t(
                            "business.fields.purchaseDiscountAccount",
                            "Akun Diskon Pembelian"
                          )}
                        </label>
                        <Select
                          name="purchaseDiscountAccount"
                          className="mt-1 text-sm"
                          classNamePrefix="select2-selection"
                          isClearable
                          options={accounts
                            .filter((account) =>
                              [
                                "COST_OF_GOODS_SOLD",
                                "EXPENSE",
                                "OTHER_EXPENSE",
                              ].includes(account.category)
                            )
                            .sort(sortAccountCode)
                            .map((account) => ({
                              value: account.id,
                              label: `${account.code} - ${account.name}`,
                            }))}
                          value={purchaseDiscountAccount}
                          onChange={setpurchaseDiscountAccount}
                          placeholder={t("Choose Account")}
                          menuPortalTarget={document.body}
                          maxMenuHeight={150}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-end bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
            <Button
              type="submit"
              loading={loading}
              disabled={loading || writeBlocked}
              className="w-full sm:w-auto"
              title={t("Save")}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
